import { Box, type BoxProps, Flex, Text } from '@chakra-ui/react';
import { Notification } from 'ui-components';
import { SOCIAL_PLATFORMS } from '~/constants';
import { formatDate } from '~/utils';

import type {
  ContentDetailMetricsContentType,
  ContentDetailMetricsItemType,
} from './ContentDetailMetrics.types';
import { getValue } from './ContentDetailMetrics.utils';
import { PopoverGlossary } from './components/PopoverGlossary';

export type IContentDetailMetricsProps<
  C extends ContentDetailMetricsContentType,
> = BoxProps & {
  content: C;
  metrics: ContentDetailMetricsItemType<C>;
  isMetricsCollaborationFFEnbled?: boolean;
};

export function ContentDetailMetrics<
  C extends ContentDetailMetricsContentType,
>({
  content,
  metrics,
  isMetricsCollaborationFFEnbled,
  ...boxProps
}: IContentDetailMetricsProps<C>) {
  const isIGCollab =
    content?.platform === SOCIAL_PLATFORMS.instagram &&
    content?.isCollaboration;

  const igContentMapping = (channel: string) =>
    channel === 'post' ? 'still' : channel;

  const renderMetrics = (metrics: any) => (
    <Flex
      flex="0 0 100%"
      marginY={2}
      marginTop={'spacing-200'}
      direction={'column'}
      paddingY={'spacing-150'}
      paddingX={'spacing-125'}
      gap={'spacing-100'}
      borderRadius="lg"
      borderWidth={1}
      borderStyle="solid"
      borderColor="primary.100"
    >
      {Object.entries(metrics).map(([metricKey, metric]) => {
        const value = getValue(content, metric as any, metricKey);
        if (metric && value !== undefined) {
          const { icon: Icon, title } = metric as any;

          return (
            <Flex paddingX={'spacing-100'} alignItems="center" key={metricKey}>
              {Icon && <Icon color="primary.400" boxSize={4} marginRight={2} />}

              <Text color="primary.400" fontSize="lg" marginRight={2}>
                {title}
              </Text>

              <Text
                marginLeft="auto"
                color="primary.500"
                fontSize="lg"
                fontWeight={500}
              >
                {value}
              </Text>
            </Flex>
          );
        }

        return null;
      })}
    </Flex>
  );

  return (
    <>
      <Box {...boxProps}>
        {isMetricsCollaborationFFEnbled && isIGCollab && (
          <Notification
            title="Collaboration post"
            variant="warning"
            marginBottom="spacing-200"
            fontSize={'md'}
          >
            The metrics data displayed is pulled only from the Talent's IG
            account, and does not reflect any metrics from other collaborators.
          </Notification>
        )}
        <Text
          flex={1}
          color="primary.300"
          fontSize="sm"
          textTransform="uppercase"
          fontWeight="medium"
          lineHeight={1}
          letterSpacing={0.24}
        >
          {content.platform !== SOCIAL_PLATFORMS.instagram
            ? `${content.platform} metrics`
            : `${content.platform} ${igContentMapping(
                content.channel,
              )} metrics`}
        </Text>
        <Box marginTop={2}>
          {Object.entries(metrics).map(([metricKey, metric]) => {
            if (metric) {
              const { icon: Icon, metrics, title, isVisible } = metric;
              const value = getValue(content, metric, metricKey);

              // Hack to show likes and comment count for off-platform
              if (
                title === 'Engagements' &&
                (content.status === 'off-platform' ||
                  content.creator.status === 'off-platform')
              ) {
                return renderMetrics(metrics);
              }

              if (value === undefined || value === null) {
                return null;
              }

              if (isVisible && !isVisible(content))
                return metrics ? renderMetrics(metrics) : null;

              return (
                <Flex
                  flexWrap="wrap"
                  alignItems="center"
                  key={metricKey}
                  paddingY={1}
                  paddingX={'spacing-100'}
                >
                  {Icon && (
                    <Icon color="primary.400" boxSize={4} marginRight={2} />
                  )}

                  <Text
                    flex={1}
                    color="primary.400"
                    fontSize="lg"
                    marginRight={2}
                    lineHeight={1.4}
                  >
                    {typeof title === 'function' ? title(content) : title}
                  </Text>

                  <Text
                    marginLeft="auto"
                    color="primary.500"
                    fontSize="lg"
                    fontWeight={500}
                    marginRight={'spacing-100'}
                  >
                    {value}
                  </Text>

                  {metrics && renderMetrics(metrics)}
                </Flex>
              );
            }
            return null;
          })}
        </Box>
      </Box>
      <Flex
        alignItems={'baseline'}
        justifyContent={'space-between'}
        marginRight={'spacing-100'}
        padding={'spacing-150'}
      >
        <PopoverGlossary metrics={metrics} content={content} />
        <Text fontSize="md" color="primary.300" textAlign="right">
          {content.insightLastUpdatedAt
            ? `Updated ${formatDate(content.insightLastUpdatedAt)}`
            : ''}
        </Text>
      </Flex>
    </>
  );
}
